import type {
  ActiveSessionPublic,
  CatalogSnapshot,
  MetricsSnapshot,
  ParticipantReceipt,
  SessionPublic,
  SessionSummary,
} from "../shared/model";

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({ error: "NETWORK_ERROR" }))) as {
      error?: string;
    };
    throw new Error(payload.error ?? `HTTP_${response.status}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const clientApi = {
  getActiveSession: () =>
    api<{ activeSession: ActiveSessionPublic | null }>("/api/active-session"),
  getSession: (slug: string) => api<SessionPublic>(`/api/sessions/${slug}`),
  getMetrics: (slug: string) => api<MetricsSnapshot>(`/api/sessions/${slug}/metrics`),
  submit: (slug: string, participantToken: string, serviceIds: string[]) =>
    api<ParticipantReceipt>(`/api/sessions/${slug}/responses`, {
      method: "POST",
      body: JSON.stringify({ participantToken, serviceIds }),
    }),
  whoAmI: () => api<{ email: string }>("/api/admin/whoami"),
  listSessions: () => api<SessionSummary[]>("/api/admin/sessions"),
  createSession: (title: string, mode: "live" | "demo") =>
    api<SessionSummary>("/api/admin/sessions", {
      method: "POST",
      body: JSON.stringify({ title, mode }),
    }),
  openSession: (slug: string) =>
    api<SessionSummary>(`/api/admin/sessions/${slug}/open`, { method: "POST" }),
  closeSession: (slug: string) =>
    api<SessionSummary>(`/api/admin/sessions/${slug}/close`, { method: "POST" }),
  deleteSession: (slug: string) =>
    api<void>(`/api/admin/sessions/${slug}`, { method: "DELETE" }),
  updateCatalog: (slug: string, catalog: CatalogSnapshot) =>
    api<SessionPublic>(`/api/admin/sessions/${slug}/catalog`, {
      method: "PATCH",
      body: JSON.stringify(catalog),
    }),
};

export function getParticipantToken(slug: string): string {
  const key = `nube:participant:${slug}`;
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const token = `${crypto.randomUUID()}${crypto.randomUUID()}`;
  localStorage.setItem(key, token);
  return token;
}

export function saveReceipt(slug: string, receipt: ParticipantReceipt): void {
  localStorage.setItem(`nube:receipt:${slug}`, JSON.stringify(receipt));
}

export function loadReceipt(slug: string): ParticipantReceipt | null {
  const raw = localStorage.getItem(`nube:receipt:${slug}`);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ParticipantReceipt;
  } catch {
    return null;
  }
}

export function liveSocketUrl(slug: string): string {
  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${location.host}/api/sessions/${slug}/live`;
}
